import React, { useEffect, useState } from 'react'
import { Drawer, Descriptions, Divider, Space, Tag, Spin, message } from 'antd'
import type { RFQ, Bid, RFQQuote } from '../types/rfq.types'
import { RFQBidsList } from './RFQBidsList'
import { rfqService } from '../../../shared/api/services/rfq.service'
import { formatIST } from '../../../shared/utils/formatters'

interface RFQDetailsDrawerProps {
    open: boolean
    onClose: () => void
    rfq: RFQ | null
    onAcceptBid: (bid: Bid) => void
    onRejectBid: (bid: Bid) => void
}

export const RFQDetailsDrawer: React.FC<RFQDetailsDrawerProps> = ({
    open,
    onClose,
    rfq,
    onAcceptBid,
    onRejectBid
}) => {
    const [quotes, setQuotes] = useState<RFQQuote[]>([])
    const [loadingQuotes, setLoadingQuotes] = useState(false)

    useEffect(() => {
        if (open && rfq?.id) {
            setLoadingQuotes(true)
            rfqService.getQuotes(rfq.id)
                .then(response => {
                    setQuotes(response.data)
                })
                .catch(() => {
                    message.error('Failed to load bids')
                })
                .finally(() => {
                    setLoadingQuotes(false)
                })
        }
    }, [open, rfq])

    if (!rfq) return null

    return (
        <Drawer
            title="RFQ Details"
            placement="right"
            width={700}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="RFQ Information" column={1} bordered>
                    <Descriptions.Item label="Ref ID">
                        <strong>#{rfq.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer">
                        {rfq.customer_name || `Customer #${rfq.customer_id}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Product">
                        <Tag color="blue">{rfq.product_name || `Product #${rfq.product_id}`}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Quantity">
                        {rfq.quantity} {rfq.unit}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pincode">{rfq.delivery_pincode}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag
                            color={
                                rfq.status === 'open'
                                    ? 'blue'
                                    : rfq.status === 'bidding'
                                        ? 'orange'
                                        : rfq.status === 'awarded'
                                            ? 'green'
                                            : 'gray'
                            }
                        >
                            {rfq.status.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Special Instructions">
                        {rfq.special_instructions || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">{formatIST(rfq.created_at)}</Descriptions.Item>
                    <Descriptions.Item label="Expires At">{formatIST(rfq.expires_at)}</Descriptions.Item>
                    <Descriptions.Item label="Required By">
                        {formatIST(rfq.required_by)}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                <div>
                    <h3>Received Bids ({quotes.length})</h3>
                    {loadingQuotes ? (
                        <Spin />
                    ) : (
                        <RFQBidsList
                            bids={quotes}
                            onAccept={onAcceptBid}
                            onReject={onRejectBid}
                        />
                    )}
                </div>
            </Space>
        </Drawer>
    )
}
